/**
 * Currency helpers — mirrors the backend `config/currency.ts`. A branch carries an ISO-4217
 * `currency` code; use these to format money instead of hardcoding ₹ / en-IN anywhere.
 */
export interface CurrencyInfo {
  code: string;
  symbol: string;
  locale: string;
  name: string;
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  INR: { code: 'INR', symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
  AED: { code: 'AED', symbol: 'د.إ', locale: 'en-AE', name: 'UAE Dirham' },
  USD: { code: 'USD', symbol: '$', locale: 'en-US', name: 'US Dollar' },
  GBP: { code: 'GBP', symbol: '£', locale: 'en-GB', name: 'British Pound' },
  EUR: { code: 'EUR', symbol: '€', locale: 'en-IE', name: 'Euro' },
  SAR: { code: 'SAR', symbol: '﷼', locale: 'en-SA', name: 'Saudi Riyal' },
};

/** Resolve currency info by code, falling back to INR for unknown/empty codes. */
export function getCurrency(code?: string | null): CurrencyInfo {
  if (!code) return CURRENCIES.INR;
  return CURRENCIES[code.toUpperCase()] || CURRENCIES.INR;
}

/**
 * Format an amount in the branch's currency. Uses Intl with the currency's locale; falls back to
 * a "<symbol><amount>" string if Intl can't format the code in this environment.
 */
export function formatMoney(amount: number | string | null | undefined, code?: string | null): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  const value = Number.isFinite(n as number) ? (n as number) : 0;
  const cur = getCurrency(code);
  try {
    return new Intl.NumberFormat(cur.locale, { style: 'currency', currency: cur.code, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${cur.symbol}${value.toFixed(2)}`;
  }
}

/** Just the symbol for a currency code (₹, $, £, …). */
export function currencySymbol(code?: string | null): string {
  return getCurrency(code).symbol;
}
